import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface User {
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
}

interface TopBarProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  user?: User | null;
  onHomeClick?: () => void;
}

export default function TopBar({
  sidebarOpen,
  onToggleSidebar,
  theme,
  onToggleTheme,
  user,
  onHomeClick
}: TopBarProps) {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  
  // Dev impersonation state
  const [showImpersonateMenu, setShowImpersonateMenu] = useState(false);
  const [impersonationUsers, setImpersonationUsers] = useState<any[]>([]);
  const [currentImpersonation, setCurrentImpersonation] = useState<string | null>(null);
  const [isDevMode, setIsDevMode] = useState(false);
  const impersonateMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (impersonateMenuRef.current && !impersonateMenuRef.current.contains(event.target as Node)) {
        setShowImpersonateMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if we're in dev mode and load impersonation state
  useEffect(() => {
    const checkDevMode = async () => {
      try {
        const res = await fetch('/api/dev/impersonate/status', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setIsDevMode(data.enabled);
          setCurrentImpersonation(data.impersonating);
        }
      } catch {
        setIsDevMode(false);
      }
    };
    checkDevMode();
  }, [user]);

  // Load available users for impersonation
  const loadImpersonationUsers = async () => {
    try {
      const res = await fetch('/api/dev/impersonate/users', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setImpersonationUsers(data.users || []);
        setCurrentImpersonation(data.current_impersonation);
      }
    } catch (err) {
      console.error('Failed to load impersonation users:', err);
    }
  };

  const handleImpersonate = async (email: string) => {
    try {
      const res = await fetch('/api/dev/impersonate/set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        setCurrentImpersonation(email);
        setShowImpersonateMenu(false);
        // Reload page to refresh user context
        window.location.reload();
      }
    } catch (err) {
      console.error('Failed to set impersonation:', err);
    }
  };

  const handleClearImpersonation = async () => {
    try {
      const res = await fetch('/api/dev/impersonate/clear', {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setCurrentImpersonation(null);
        setShowImpersonateMenu(false);
        // Reload page to refresh user context
        window.location.reload();
      }
    } catch (err) {
      console.error('Failed to clear impersonation:', err);
    }
  };

  const getUserInitials = () => {
    if (!user?.email) return 'U';
    const parts = user.email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  return (
    <header style={{
      padding: '14px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: 'transparent'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Sidebar toggle + logo when sidebar is closed */}
        {!sidebarOpen && (
          <>
            <button
              onClick={onToggleSidebar}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '34px',
                height: '34px',
                borderRadius: '8px',
                border: '1px solid var(--border)',
                background: 'var(--surface2)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'var(--surface)';
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.color = 'var(--text)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'var(--surface2)';
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.color = 'var(--text-muted)';
              }}
              title="Open sidebar"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
            <button
              type="button"
              onClick={() => {
                if (onHomeClick) {
                  onHomeClick();
                } else {
                  navigate('/');
                }
              }}
              title="Home"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'inherit'
              }}
            >
              <img src="/pearson_new.png" alt="Pearson" style={{ width: '32px', height: '32px', borderRadius: '9px' }} />
              <div style={{ fontSize: '15px', whiteSpace: 'nowrap' }}>
                <span style={{ fontWeight: 700, color: 'var(--text)' }}>Pearson</span>
                <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> Analyst</span>
              </div>
            </button>
          </>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {/* Theme toggle */}
        <button onClick={onToggleTheme} title="Toggle theme" style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          border: 'none',
          background: 'transparent',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'all 0.15s'
        }}
          onMouseOver={(e) => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'var(--surface2)'; }}
          onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
        >
          {theme === 'light' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '17px', height: '17px' }}>
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '17px', height: '17px' }}>
              <circle cx="12" cy="12" r="5"/>
              <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          )}
        </button>

        {/* Dev Impersonation (only in local dev) */}
        {isDevMode && (
          <div ref={impersonateMenuRef} style={{ position: 'relative' }}>
            <button 
              onClick={() => {
                setShowImpersonateMenu(!showImpersonateMenu);
                if (!showImpersonateMenu) {
                  loadImpersonationUsers();
                }
              }}
              title={currentImpersonation ? `Impersonating: ${currentImpersonation}` : 'Impersonate user (dev)'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                border: currentImpersonation ? '1.5px solid var(--accent)' : 'none',
                background: currentImpersonation ? 'var(--accent-dim)' : 'transparent',
                color: currentImpersonation ? 'var(--accent)' : 'var(--text-muted)',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'all 0.15s',
                position: 'relative'
              }}
              onMouseOver={(e) => { 
                if (!currentImpersonation) {
                  e.currentTarget.style.color = 'var(--text)'; 
                  e.currentTarget.style.background = 'var(--surface2)'; 
                }
              }}
              onMouseOut={(e) => { 
                if (!currentImpersonation) {
                  e.currentTarget.style.color = 'var(--text-muted)'; 
                  e.currentTarget.style.background = 'transparent'; 
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '17px', height: '17px' }}>
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <polyline points="17 11 19 13 23 9"/>
              </svg>
              {currentImpersonation && (
                <span style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'var(--accent)',
                  border: '1.5px solid var(--bg)'
                }} />
              )}
            </button>

            {showImpersonateMenu && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                minWidth: '280px',
                maxHeight: '400px',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                zIndex: 1000,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Header */}
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--border)',
                  background: 'var(--surface2)'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
                    🎭 Dev Impersonation
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    Test as different users
                  </div>
                </div>

                {/* Current impersonation */}
                {currentImpersonation && (
                  <div style={{
                    padding: '10px 16px',
                    background: 'var(--accent-dim)',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--text)', flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, marginBottom: '2px' }}>Currently impersonating:</div>
                      <div style={{ 
                        fontSize: '11px', 
                        color: 'var(--text-muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {currentImpersonation}
                      </div>
                    </div>
                    <button
                      onClick={handleClearImpersonation}
                      style={{
                        padding: '4px 10px',
                        background: 'var(--surface)',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        color: 'var(--text)',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        flexShrink: 0
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}

                {/* User list */}
                <div style={{ 
                  overflowY: 'auto', 
                  flex: 1,
                  padding: '4px'
                }}>
                  {impersonationUsers.length === 0 ? (
                    <div style={{ 
                      padding: '20px 16px', 
                      textAlign: 'center', 
                      color: 'var(--text-muted)',
                      fontSize: '12px'
                    }}>
                      No users available
                    </div>
                  ) : (
                    impersonationUsers.map((u) => (
                      <button
                        key={u.email}
                        onClick={() => handleImpersonate(u.email)}
                        disabled={u.email === currentImpersonation}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          background: u.email === currentImpersonation ? 'var(--accent-dim)' : 'transparent',
                          border: 'none',
                          color: 'var(--text)',
                          fontSize: '12px',
                          cursor: u.email === currentImpersonation ? 'default' : 'pointer',
                          borderRadius: '8px',
                          transition: 'background 0.15s',
                          textAlign: 'left',
                          opacity: u.email === currentImpersonation ? 0.6 : 1
                        }}
                        onMouseOver={(e) => { 
                          if (u.email !== currentImpersonation) {
                            e.currentTarget.style.background = 'var(--surface2)'; 
                          }
                        }}
                        onMouseOut={(e) => { 
                          if (u.email !== currentImpersonation) {
                            e.currentTarget.style.background = 'transparent'; 
                          }
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: 600, 
                            marginBottom: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {u.display_name}
                          </div>
                          <div style={{ 
                            fontSize: '11px', 
                            color: 'var(--text-muted)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {u.role?.toUpperCase() || 'VIEWER'}
                          </div>
                        </div>
                        {u.is_admin && (
                          <span style={{
                            padding: '2px 6px',
                            background: 'var(--accent)',
                            color: '#fff',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 600,
                            flexShrink: 0,
                            marginLeft: '8px'
                          }}>
                            ADMIN
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Glossary */}
        <button onClick={() => navigate('/glossary')} title="Glossary & guidance" style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          border: 'none',
          background: 'transparent',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'all 0.15s'
        }}
          onMouseOver={(e) => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'var(--surface2)'; }}
          onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '17px', height: '17px' }}>
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </button>
        
        {/* User avatar with dropdown */}
        <div ref={userMenuRef} style={{ position: 'relative' }}>
          <button 
            onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: '2px solid var(--accent)',
              background: 'var(--accent)',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'all 0.2s',
              boxShadow: showUserMenu ? '0 0 0 3px var(--accent-dim)' : 'none'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title={user?.email || 'User'}
          >
            {getUserInitials()}
          </button>
          {showUserMenu && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              minWidth: '220px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              overflow: 'hidden'
            }}>
              {/* User info */}
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface2)'
              }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
                  {user?.first_name && user?.last_name 
                    ? `${user.first_name} ${user.last_name}`
                    : user?.email || 'User'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {user?.email}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Role: {user?.role?.toUpperCase() || 'VIEWER'}
                </div>
              </div>

              {/* Menu items */}
              <div style={{ padding: '4px' }}>
                  <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/settings');
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text)',
                    fontSize: '13px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    transition: 'background 0.15s',
                    textAlign: 'left'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface2)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 1v6m0 6v6m9-9h-6m-6 0H3"/>
                    <path d="M19.14 19.14l-4.24-4.24m0-5.66l4.24-4.24M4.86 19.14l4.24-4.24m0-5.66L4.86 4.86"/>
                  </svg>
                  Settings
                </button>
                <button
                  onClick={async () => {
                    try {
                      await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
                      navigate('/login');
                    } catch (err) {
                      console.error('Logout failed:', err);
                    }
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--red)',
                    fontSize: '13px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    transition: 'background 0.15s',
                    textAlign: 'left'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface2)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
